import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Calculator, Plus, Trash2, Save, Pencil, X, FolderDown, FolderUp, Layers, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import HelpHint from './HelpHint';
import { AcquisitionExpenses } from './AcquisitionExpenses';
import { hasRealRoiVenta, RoiValue } from './RoiDisplay';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TIPOS_OPERATIVO = [
  { value: 'CREDITO', label: 'Crédito' },
  { value: 'SUBASTA', label: 'Subasta' },
  { value: 'REMATE', label: 'Remate' },
  { value: 'COMPRA_DIRECTA', label: 'Compra Directa' },
  { value: 'OCUPADO', label: 'Ocupado' },
];

const CATEGORIAS_GASTO = [
  { value: 'impuestos', label: 'Impuestos' },
  { value: 'adjudicacion', label: 'Adjudicación' },
  { value: 'juridico', label: 'Jurídico' },
  { value: 'registral', label: 'Registral' },
  { value: 'posesion', label: 'Posesión' },
  { value: 'reforma', label: 'Reforma' },
  { value: 'comercializacion', label: 'Comercialización' },
  { value: 'otros', label: 'Otros' },
];

const cur = (v) => v != null ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v) : '-';
const cur2 = (v) => v != null ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(v) : '-';
const pct = (v) => v != null ? `${v > 0 ? '+' : ''}${v.toFixed(2)}%` : '-';

const ROIAdvancedTab = ({ asset, assetId, onAssetUpdated, readOnly }) => {
  const [form, setForm] = useState({
    tipo_activo_operativo: '', precio_compra: 0, deuda_total: 0, valor_adjudicacion: 0,
    valor_referencia: 0, valor_salida_base: 0, tiempo_resolucion_meses: 0,
    fecha_compra: '', fecha_venta: '',
    tasacion: 0, vivienda_habitual: null, coste_posesion: 0,
  });

  const [itpEnabled, setItpEnabled] = useState(false);
  const [ajdEnabled, setAjdEnabled] = useState(false);
  const [ivaEnabled, setIvaEnabled] = useState(false);
  const [itpRate, setItpRate] = useState(0);
  const [ajdRate, setAjdRate] = useState(0);
  const [ivaRate, setIvaRate] = useState(0);
  const [itpBaseSource, setItpBaseSource] = useState('auto');
  const [ajdBaseSource, setAjdBaseSource] = useState('auto');
  const [ivaBaseSource, setIvaBaseSource] = useState('auto');

  const [gastos, setGastos] = useState([]);
  const [newGasto, setNewGasto] = useState({ concepto: '', importe: '', categoria: 'otros' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ concepto: '', importe: 0, categoria: 'otros' });
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { setResult(null); }, [assetId, asset?.roi_calculado_at]);
  const [exporting, setExporting] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [calculatingTaxes, setCalculatingTaxes] = useState(false);

  const seedCalledRef = useRef(false);

  useEffect(() => {
    if (!asset) return;
    setForm({
      tipo_activo_operativo: asset.tipo_activo_operativo || '',
      precio_compra: asset.precio_compra || 0,
      deuda_total: asset.deuda_total || asset.deuda_reclamada || 0,
      valor_adjudicacion: asset.valor_adjudicacion || 0,
      valor_referencia: asset.valor_referencia || 0,
      // Priority: valor_salida_base (individual with adjustments) > valor_salida_base (auto) > tasacion
      valor_salida_base: asset.valor_salida_base || asset.valor_salida_base || asset.tasacion || 0,
      tiempo_resolucion_meses: asset.tiempo_resolucion_meses || 0,
      fecha_compra: asset.fecha_compra || '',
      fecha_venta: asset.fecha_venta || '',
      tasacion: asset.tasacion || 0,
      vivienda_habitual: asset.vivienda_habitual || null,
      coste_posesion: asset.coste_posesion || 0,
    });
    if (asset.tipo_impositivo_itp > 0) { setItpEnabled(true); setItpRate(asset.tipo_impositivo_itp); }
    if (asset.tipo_impositivo_ajd > 0) { setAjdEnabled(true); setAjdRate(asset.tipo_impositivo_ajd); }
    if (asset.tipo_impositivo_iva > 0) { setIvaEnabled(true); setIvaRate(asset.tipo_impositivo_iva); }
  }, [asset]);

  const fetchGastos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assets/${assetId}/gastos`, { withCredentials: true });
      setGastos(res.data.items ?? res.data);
    } catch {}
  }, [assetId]);

  useEffect(() => { fetchGastos(); }, [fetchGastos]);

  useEffect(() => {
    if (readOnly || !assetId || seedCalledRef.current) return;
    seedCalledRef.current = true;
    const doSeed = async () => {
      try {
        const res = await axios.post(`${API}/assets/${assetId}/seed-gastos`, {}, { withCredentials: true });
        if (res.data.seeded && res.data.added?.length > 0) {
          fetchGastos();
        }
      } catch {}
    };
    doSeed();
  }, [assetId, fetchGastos, readOnly]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/expense-templates`, { withCredentials: true });
      setTemplates(res.data.items ?? res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const gastosTotal = gastos.reduce((s, g) => s + g.importe, 0);

  // Exclusivity: ITP and IVA cannot coexist. Wrap the setters so toggling one disables the other.
  const toggleItp = (enabled) => {
    setItpEnabled(enabled);
    if (enabled) setIvaEnabled(false);
  };
  const toggleIva = (enabled) => {
    setIvaEnabled(enabled);
    if (enabled) setItpEnabled(false);
  };

  const applyAutomaticTaxes = async () => {
    if (readOnly || !assetId) return;
    setCalculatingTaxes(true);
    try {
      const res = await axios.post(`${API}/assets/${assetId}/calculate-taxes`, {}, { withCredentials: true });
      const { impuesto, tipo, base } = res.data;
      // Reset all, then apply the one returned by the backend (respecting exclusivity).
      setItpEnabled(false); setAjdEnabled(false); setIvaEnabled(false);
      setItpRate(0); setAjdRate(0); setIvaRate(0);
      if (impuesto === 'ITP') { setItpEnabled(true); setItpRate(tipo); }
      else if (impuesto === 'AJD') { setAjdEnabled(true); setAjdRate(tipo); }
      else if (impuesto === 'IVA') { setIvaEnabled(true); setIvaRate(tipo); }
      toast.success(`${impuesto} ${tipo}% sobre ${new Intl.NumberFormat('es-ES',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(base)} aplicado según reglas`);
      if (onAssetUpdated) onAssetUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al calcular impuestos');
    } finally { setCalculatingTaxes(false); }
  };

  const getBaseImponible = (source, impuesto) => {
    const pc = form.precio_compra || 0;
    const vr = form.valor_referencia || 0;
    const va = form.valor_adjudicacion || 0;
    const dt = form.deuda_total || 0;
    if (source === 'precio_compra') return pc;
    if (source === 'valor_referencia') return vr;
    if (source === 'valor_adjudicacion') return va;
    if (source === 'deuda_total') return dt;
    if (source === 'max_deuda_precio') return Math.max(dt, pc);
    if (source === 'max_adj_valref') return Math.max(va, vr);
    if (source === 'adj_or_precio') return va > 0 ? va : pc;
    if (impuesto === 'ajd') return Math.max(dt, pc);
    return Math.max(pc, vr);
  };

  const baseItp = itpEnabled ? getBaseImponible(itpBaseSource, 'itp') : 0;
  const baseAjd = ajdEnabled ? getBaseImponible(ajdBaseSource, 'ajd') : 0;
  const baseIva = ivaEnabled ? getBaseImponible(ivaBaseSource, 'iva') : 0;
  const itpAmount = Math.round(baseItp * (itpRate || 0) / 100 * 100) / 100;
  const ajdAmount = Math.round(baseAjd * (ajdRate || 0) / 100 * 100) / 100;
  const ivaAmount = Math.round(baseIva * (ivaRate || 0) / 100 * 100) / 100;
  const totalImpuestos = itpAmount + ajdAmount + ivaAmount;

  // Coste total local que imita al backend:
  // - SUBASTA: valor_adjudicacion + impuestos + gastos
  // - OCUPADO: (precio_compra || valor_adjudicacion) + impuestos + gastos + coste_posesion
  // - CREDITO / REMATE / COMPRA_DIRECTA (y default): precio_compra + impuestos + gastos
  const tipoOp = form.tipo_activo_operativo;
  const baseCompra =
    tipoOp === 'SUBASTA'
      ? (form.valor_adjudicacion || 0)
      : tipoOp === 'OCUPADO'
        ? ((form.precio_compra || 0) || (form.valor_adjudicacion || 0))
        : (form.precio_compra || 0);
  const extraOcupado = tipoOp === 'OCUPADO' ? (form.coste_posesion || 0) : 0;
  const costeTotal = baseCompra + totalImpuestos + gastosTotal + extraOcupado;
  const equity = (form.valor_salida_base || 0) - costeTotal;
  const roiTotal = costeTotal > 0 ? (equity / costeTotal) * 100 : 0;

  // ROI anualizado estimado (por meses)
  const tiempoMeses = form.tiempo_resolucion_meses || 0;
  const roiAnualizadoEstimado = tiempoMeses > 0 && costeTotal > 0 ? roiTotal * (12 / tiempoMeses) : null;

  const addGasto = async () => {
    if (!newGasto.concepto || !newGasto.importe) return;
    try {
      await axios.post(`${API}/assets/${assetId}/gastos`, { asset_id: assetId, concepto: newGasto.concepto, importe: parseFloat(newGasto.importe) || 0, categoria: newGasto.categoria }, { withCredentials: true });
      setNewGasto({ concepto: '', importe: '', categoria: 'otros' });
      fetchGastos();
      toast.success('Gasto añadido');
    } catch { toast.error('Error al añadir'); }
  };

  const deleteGasto = async (gid) => {
    try { await axios.delete(`${API}/assets/${assetId}/gastos/${gid}`, { withCredentials: true }); fetchGastos(); } catch { toast.error('Error'); }
  };

  const startEdit = (g) => { setEditingId(g.id); setEditForm({ concepto: g.concepto, importe: g.importe, categoria: g.categoria }); };

  const saveEdit = async (gid) => {
    try {
      await axios.put(`${API}/assets/${assetId}/gastos/${gid}`, { asset_id: assetId, ...editForm }, { withCredentials: true });
      setEditingId(null);
      fetchGastos();
      toast.success('Gasto actualizado');
    } catch { toast.error('Error al guardar'); }
  };

  const applyTemplate = async () => {
    if (!selectedTemplateId) return;
    try {
      const res = await axios.post(`${API}/assets/${assetId}/apply-template/${selectedTemplateId}`, {}, { withCredentials: true });
      if (res.data.added?.length > 0) {
        toast.success(`Añadidas ${res.data.added.length} líneas`);
        fetchGastos();
      } else {
        toast.info('Todas las líneas ya existen');
      }
    } catch { toast.error('Error al aplicar plantilla'); }
  };

  const saveAsTemplate = async () => {
    if (!newTemplateName.trim()) { toast.error('Nombre requerido'); return; }
    if (gastos.length === 0) { toast.error('No hay gastos'); return; }
    try {
      const items = gastos.map(g => ({ concepto: g.concepto, importe: g.importe, categoria: g.categoria }));
      await axios.post(`${API}/expense-templates`, { name: newTemplateName.trim(), tipo_activo_operativo: form.tipo_activo_operativo || null, items }, { withCredentials: true });
      toast.success('Plantilla guardada');
      setShowSaveTemplate(false);
      setNewTemplateName('');
      fetchTemplates();
    } catch { toast.error('Error al guardar plantilla'); }
  };

  const deleteTemplate = async (tid) => {
    try {
      await axios.delete(`${API}/expense-templates/${tid}`, { withCredentials: true });
      toast.success('Plantilla eliminada');
      fetchTemplates();
      if (selectedTemplateId === tid) setSelectedTemplateId('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const [roiErrors, setRoiErrors] = useState({});

  const handleCalculate = async () => {
    const missing = {};
    if (!form.tipo_activo_operativo) missing.tipo_activo_operativo = 'Tipo activo operativo';
    if (!form.precio_compra) missing.precio_compra = 'Precio de compra';
    if (!form.valor_salida_base) missing.valor_salida_base = 'Valor salida estimado';
    if (!form.tiempo_resolucion_meses) missing.tiempo_resolucion_meses = 'Tiempo estimado (meses)';
    if (Object.keys(missing).length > 0) {
      setRoiErrors(missing);
      toast.error('Faltan campos obligatorios: ' + Object.values(missing).join(', ') + '. Editalos desde el boton Editar del activo.');
      return;
    }
    setRoiErrors({});
    setCalculating(true);
    try {
      const res = await axios.post(`${API}/assets/${assetId}/calculate-roi-advanced`, {
        ...form,
        fecha_compra: form.fecha_compra || null,
        fecha_venta: form.fecha_venta || null,
        tipo_impositivo_itp: itpEnabled ? itpRate : 0,
        tipo_impositivo_ajd: ajdEnabled ? ajdRate : 0,
        tipo_impositivo_iva: ivaEnabled ? ivaRate : 0,
        base_imponible_itp_manual: itpEnabled ? baseItp : 0,
        base_imponible_ajd: ajdEnabled ? baseAjd : 0,
        base_imponible_iva: ivaEnabled ? baseIva : 0,
      }, { withCredentials: true });
      setResult(res.data);
      if (onAssetUpdated) onAssetUpdated();
      toast.success('Análisis guardado');
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setCalculating(false); }
  };

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfDescription, setPdfDescription] = useState('');
  const [pdfPhotos, setPdfPhotos] = useState([]); // [{id, nombre, url, selected}]

  const openExportModal = async () => {
    setPdfDescription('');
    setPdfPhotos([]);
    setPdfModalOpen(true);
    // Cargamos SOLO documentos de tipo imagen desde la pestaña Documentos.
    try {
      const r = await axios.get(`${API}/assets/${assetId}/documents?limit=200`, { withCredentials: true });
      const items = r.data?.items || [];
      const imgs = items
        .filter(d => ((d.content_type || d.mime_type || '').toLowerCase()).startsWith('image/'))
        .map(d => ({
          id: d.id,
          nombre: d.nombre || d.original_filename || 'Foto',
          url: `${API}/documents/${d.id}/download?inline=true`,
          selected: false,
        }));
      setPdfPhotos(imgs);
    } catch (err) {
      // Silencioso: si falla el listado, el modal sigue funcional sin fotos.
    }
  };

  const togglePhotoSelected = (idx) => {
    setPdfPhotos(prev => {
      const already = prev[idx].selected;
      if (!already) {
        const currentCount = prev.filter(p => p.selected).length;
        if (currentCount >= 3) {
          toast.info('Máximo 3 fotos.');
          return prev;
        }
      }
      return prev.map((p, i) => i === idx ? { ...p, selected: !p.selected } : p);
    });
  };

  const exportRoiPdf = async () => {
    if (pdfDescription && pdfDescription.length > 600) {
      toast.error('La descripción no puede superar 600 caracteres');
      return;
    }
    setExporting(true);
    try {
      const selectedPhotoIds = pdfPhotos.filter(p => p.selected).map(p => p.id).slice(0, 3);
      const payload = {
        opportunity_description: pdfDescription || undefined,
        photo_ids: selectedPhotoIds.length ? selectedPhotoIds : undefined,
      };
      const res = await axios.post(
        `${API}/assets/${assetId}/roi-export-pdf`,
        payload,
        { withCredentials: true, responseType: 'blob' },
      );
      // Extraer nombre del header Content-Disposition (formato XÉNICOS).
      let filename = `roi_${assetId.slice(0, 8)}.pdf`;
      const cd = res.headers['content-disposition'] || res.headers['Content-Disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      if (m) filename = decodeURIComponent(m[1]);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setPdfModalOpen(false);
      toast.success('PDF exportado');
    } catch (err) {
      toast.error('Error al exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  const tipo = form.tipo_activo_operativo;

  // Resolve base imponible €. For AJD (used when tipo operativo = CREDITO) the default
  // is "max(deuda_total, precio_compra)"; for the rest, "max(precio_compra, valor_referencia)".

  const BaseSelector = ({ source, setSource, label }) => {
    // Base options depend on which tax this row represents.
    const commonOptions = [
      { value: 'precio_compra', labelText: `Precio compra (${cur(form.precio_compra)})` },
      { value: 'valor_referencia', labelText: `Valor ref. (${cur(form.valor_referencia)})` },
    ];
    let options;
    if (label === 'ajd') {
      options = [
        { value: 'auto', labelText: 'Mayor entre Deuda y Precio compra (auto)' },
        { value: 'max_deuda_precio', labelText: `Mayor Deuda/Precio (${cur(Math.max(form.deuda_total || 0, form.precio_compra || 0))})` },
        { value: 'deuda_total', labelText: `Deuda reclamada (${cur(form.deuda_total)})` },
        { value: 'precio_compra', labelText: `Precio compra (${cur(form.precio_compra)})` },
      ];
    } else {
      // ITP / IVA — include valor_adjudicacion as optional when SUBASTA/REMATE/CREDITO
      options = [
        { value: 'auto', labelText: 'Mayor valor (auto)' },
        ...commonOptions,
      ];
      if ((form.valor_adjudicacion || 0) > 0) {
        options.push({ value: 'valor_adjudicacion', labelText: `Valor adjudicación (${cur(form.valor_adjudicacion)})` });
      }
    }
    return (
      <select value={source} onChange={e => setSource(e.target.value)} className="input-base text-sm mt-1" data-testid={`base-${label}`}>
        {options.map(o => <option key={o.value} value={o.value}>{o.labelText}</option>)}
      </select>
    );
  };

  return (
    <div className="space-y-6" data-testid="roi-advanced-tab">
      <div className="card-base p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="heading-3 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-600" />
            Análisis de Rentabilidad
          </h3>
          <div className="flex items-center gap-2">
            {!readOnly && (
            <button onClick={openExportModal} disabled={exporting} className="btn-secondary text-sm px-3 py-1.5 flex items-center gap-1.5" data-testid="roi-export-pdf-btn">
              <FileDown className="w-3.5 h-3.5" /> {exporting ? 'Exportando...' : 'Exportar PDF'}
            </button>
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400 mb-5">Los datos base se editan desde el boton Editar del activo.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          <RO label="Tipo Activo Operativo" help="Determina el motor de cálculo aplicado: CRÉDITO usa ROI sobre deuda, SUBASTA/REMATE sobre adjudicación, COMPRA_DIRECTA sobre precio directo, OCUPADO añade coste de desalojo." value={form.tipo_activo_operativo || '-'} tid="roi-tipo-operativo" error={roiErrors.tipo_activo_operativo} />
          <RO label={`Precio de Compra${asset?.precio_compra_es_estimado ? ' *' : ''}`} help="Precio al que XÉNICOS adquiere el activo. Base del coste total. El asterisco (*) indica que es una estimación automática." value={`${cur(form.precio_compra)}${asset?.precio_compra_es_estimado ? ' *' : ''}`} tid="roi-precio" error={roiErrors.precio_compra} />
          <RO label="Valor de Referencia" help="Valor de referencia catastral. Base fiscal por defecto para ITP / AJD." value={cur(form.valor_referencia)} tid="roi-referencia" />
          <RO label={asset?.valoracion_resultado ? "Valor Salida (individualizado)" : "Valor Salida Estimado"} help="Precio de salida a mercado (venta). Si hay una valoración individualizada en la pestaña Venta se usa ese valor; si no, la estimación básica." value={cur(form.valor_salida_base)} tid="roi-salida" error={roiErrors.valor_salida_base} />
          <RO label="Tiempo estimado (meses)" help="Plazo hasta la venta. Se usa para anualizar el ROI (ROI anualizado = ROI × 12 / meses)." value={form.tiempo_resolucion_meses || '-'} tid="roi-tiempo" error={roiErrors.tiempo_resolucion_meses} />
          <RO label="Fecha de Compra" help="Fecha efectiva de adquisición del activo. Junto con la fecha de venta define los días reales de operación." value={form.fecha_compra || 'Sin definir'} tid="roi-fecha-compra" />
          <div className="space-y-1">
            <label className="text-sm text-slate-600">Fecha de Venta<HelpHint text="Fecha estimada o real de venta. Si está vacía y hay fecha de compra, el ROI se calcula 'en curso' hasta hoy." /></label>
            <p className="text-sm font-medium text-slate-800 py-2" data-testid="roi-fecha-venta">{form.fecha_venta || 'Sin definir'}</p>
            {!form.fecha_venta && form.fecha_compra && <p className="text-xs text-amber-600">En curso (hasta hoy)</p>}
          </div>
          {tipo !== 'COMPRA_DIRECTA' && <RO label="Tasacion" help="Valor de tasación oficial. Sirve como base fiscal alternativa (BaseSelector) para ITP / AJD si es mayor que el precio." value={cur(form.tasacion)} tid="roi-tasacion" />}
          {tipo === 'CREDITO' && <RO label="Deuda Total" help="Deuda pendiente sobre el activo. En operaciones tipo CRÉDITO forma parte del coste total de recuperación." value={cur(form.deuda_total)} tid="roi-deuda" />}
          {(tipo === 'SUBASTA' || tipo === 'CREDITO') && <RO label="Valor Adjudicacion" help="Precio de adjudicación en subasta o adjudicación judicial. En SUBASTA es el propio precio de compra; en CRÉDITO se usa como base fiscal alternativa." value={cur(form.valor_adjudicacion)} tid="roi-adj" />}
          {tipo === 'OCUPADO' && <RO label="Coste Posesion/Desalojo" help="Coste estimado del proceso de desalojo (letrados, procurador, procedimiento judicial, ejecución). Se suma al coste total." value={cur(form.coste_posesion)} tid="roi-posesion" />}
          {(tipo === 'SUBASTA' || tipo === 'CREDITO') && (
            <RO label="Vivienda Habitual" help="Indica si el inmueble es vivienda habitual del deudor. Habitual usa el 70% de tasación como base fiscal; no habitual el 50%." value={form.vivienda_habitual === 'NO' ? 'No (50%)' : form.vivienda_habitual === 'SI' ? 'Sí (70%)' : 'No consta (70%)'} tid="roi-vivienda" />
          )}
        </div>
      </div>

      {/* IMPUESTOS */}
      <div className="card-base p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <h3 className="heading-3">Impuestos</h3>
          {!readOnly && (
            <button
              onClick={applyAutomaticTaxes}
              disabled={calculatingTaxes}
              className="btn-secondary text-sm flex items-center gap-2"
              data-testid="roi-calc-taxes-btn"
              title="Aplica las reglas de impuestos definidas en Parámetros › Impuestos"
            >
              {calculatingTaxes ? <Calculator className="w-4 h-4 animate-pulse" /> : <Calculator className="w-4 h-4" />}
              Calcular impuestos
            </button>
          )}
        </div>
        <div className="space-y-4">
          <TaxRow label="ITP" help="Impuesto de Transmisiones Patrimoniales. Se aplica en transmisiones entre particulares o cuando el vendedor no es sujeto pasivo de IVA. Incompatible con IVA." enabled={itpEnabled} setEnabled={toggleItp} rate={itpRate} setRate={setItpRate} base={baseItp} amount={itpAmount}>
            {itpEnabled && <BaseSelector source={itpBaseSource} setSource={setItpBaseSource} label="itp" />}
          </TaxRow>
          <TaxRow label="AJD" help="Actos Jurídicos Documentados. Impuesto sobre la escritura pública. Suele aplicarse junto al IVA (no con ITP)." enabled={ajdEnabled} setEnabled={setAjdEnabled} rate={ajdRate} setRate={setAjdRate} base={baseAjd} amount={ajdAmount}>
            {ajdEnabled && <BaseSelector source={ajdBaseSource} setSource={setAjdBaseSource} label="ajd" />}
          </TaxRow>
          <TaxRow label="IVA" help="Impuesto sobre el Valor Añadido. Se aplica cuando el vendedor es empresario/profesional y el bien está sujeto al IVA. Incompatible con ITP." enabled={ivaEnabled} setEnabled={toggleIva} rate={ivaRate} setRate={setIvaRate} base={baseIva} amount={ivaAmount} ivaRates={[21, 10, 0]}>
            {ivaEnabled && <BaseSelector source={ivaBaseSource} setSource={setIvaBaseSource} label="iva" />}
          </TaxRow>
          {(itpEnabled || ivaEnabled) && (
            <p className="text-xs text-slate-400">ITP e IVA no se aplican simultáneamente. Al activar uno se desactiva el otro.</p>
          )}
          <div className="flex justify-between pt-3 border-t border-slate-200">
            <span className="font-medium text-slate-700">Total Impuestos</span>
            <span className="font-semibold text-slate-900">{cur2(totalImpuestos)}</span>
          </div>
        </div>
      </div>

      {/* GASTOS */}
      <div className="card-base p-6">
        <div className="flex flex-wrap gap-2 items-center justify-between mb-4">
          <h3 className="heading-3">Líneas de Gasto</h3>
          <span className="badge-gold px-3 py-1 text-sm" data-testid="roi-gastos-total">Total: {cur2(gastosTotal)}</span>
        </div>

        {/* Templates bar */}
        {!readOnly && <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-slate-100" data-testid="templates-bar">
          <Layers className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <select value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)} className="input-base text-sm w-auto max-w-[220px]" data-testid="template-select">
            <option value="">Plantilla...</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}{t.is_system ? ' (sistema)' : ''}</option>)}
          </select>
          <button onClick={applyTemplate} disabled={!selectedTemplateId} className="btn-secondary px-3 py-1.5 text-sm flex items-center gap-1 disabled:opacity-40" data-testid="apply-template-btn">
            <FolderDown className="w-3.5 h-3.5" /> Cargar
          </button>
          {selectedTemplateId && (() => {
            const tmpl = templates.find(t => t.id === selectedTemplateId);
            return tmpl && !tmpl.is_system ? (
              <button onClick={() => deleteTemplate(selectedTemplateId)} className="text-red-400 hover:text-red-600 p-1" title="Eliminar plantilla" data-testid="delete-template-btn"><Trash2 className="w-3.5 h-3.5" /></button>
            ) : null;
          })()}
          <div className="flex-1" />
          {!showSaveTemplate ? (
            <button onClick={() => setShowSaveTemplate(true)} disabled={gastos.length === 0} className="btn-secondary px-3 py-1.5 text-sm flex items-center gap-1 disabled:opacity-40" data-testid="save-template-btn">
              <FolderUp className="w-3.5 h-3.5" /> Guardar plantilla
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <input className="input-base text-sm w-40" value={newTemplateName} onChange={e => setNewTemplateName(e.target.value)} placeholder="Nombre plantilla" data-testid="template-name-input" onKeyDown={e => e.key === 'Enter' && saveAsTemplate()} />
              <button onClick={saveAsTemplate} className="text-emerald-600 hover:text-emerald-800 p-1" title="Guardar"><Save className="w-4 h-4" /></button>
              <button onClick={() => { setShowSaveTemplate(false); setNewTemplateName(''); }} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-4 h-4" /></button>
            </div>
          )}
        </div>}

        {/* Column headers */}
        <div className="hidden md:grid md:grid-cols-[minmax(0,1fr)_110px_100px_72px] gap-2 px-2 pb-1 text-xs text-slate-400 uppercase tracking-wider" data-testid="gasto-headers">
          <span>Concepto</span>
          <span className="text-right">Importe</span>
          <span className="text-center">Categoría</span>
          <span className="text-center">Acciones</span>
        </div>

        {/* Expense lines */}
        {gastos.map(g => (
          <div key={g.id} className="grid grid-cols-2 md:grid-cols-[minmax(0,1fr)_110px_100px_72px] gap-2 items-center p-2 border border-slate-200 rounded mb-1 min-w-0" data-testid={`gasto-row-${g.id}`}>
            {editingId === g.id ? (
              <>
                <input className="input-base text-sm" value={editForm.concepto} onChange={e => setEditForm({...editForm, concepto: e.target.value})} data-testid="edit-gasto-concepto" />
                <input type="number" className="input-base text-sm text-right" value={editForm.importe} onChange={e => setEditForm({...editForm, importe: parseFloat(e.target.value) || 0})} data-testid="edit-gasto-importe" />
                <select className="input-base text-xs" value={editForm.categoria} onChange={e => setEditForm({...editForm, categoria: e.target.value})} data-testid="edit-gasto-categoria">
                  {CATEGORIAS_GASTO.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <div className="flex items-center justify-center gap-1">
                  <button onClick={() => saveEdit(g.id)} className="text-emerald-600 hover:text-emerald-800 p-1" title="Guardar" data-testid="save-edit-btn"><Save className="w-4 h-4" /></button>
                  <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-slate-600 p-1" title="Cancelar"><X className="w-4 h-4" /></button>
                </div>
              </>
            ) : (
              <>
                <span className="text-sm font-medium text-slate-700 [overflow-wrap:anywhere] min-w-0" title={g.concepto} data-testid={`gasto-concepto-text-${g.id}`}>{g.concepto}</span>
                <span className="text-sm font-semibold text-slate-900 text-right [overflow-wrap:anywhere] min-w-0" data-testid={`gasto-importe-text-${g.id}`}>{cur2(g.importe)}</span>
                <span className="text-xs text-slate-500 md:text-center [overflow-wrap:anywhere] min-w-0" data-testid={`gasto-categoria-text-${g.id}`}>{g.categoria}</span>
                <div className="flex items-center justify-center gap-1">
                  {!readOnly && <button onClick={() => startEdit(g)} className="text-slate-400 hover:text-blue-600 p-1" title="Editar" data-testid={`edit-gasto-btn-${g.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
                  {!readOnly && <button onClick={() => deleteGasto(g.id)} className="text-slate-400 hover:text-red-600 p-1" title="Eliminar" data-testid={`delete-gasto-btn-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </>
            )}
          </div>
        ))}

        {gastos.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-4">No hay líneas de gasto. Usa una plantilla o añade manualmente.</p>
        )}

        {/* Add row - same grid */}
        {!readOnly && <div className="grid grid-cols-2 md:grid-cols-[minmax(0,1fr)_110px_100px_72px] gap-2 items-center mt-3 pt-3 border-t border-slate-100">
          <input className="input-base text-sm" value={newGasto.concepto} onChange={e => setNewGasto({...newGasto, concepto: e.target.value})} placeholder="Concepto del gasto" data-testid="gasto-concepto" />
          <input type="number" className="input-base text-sm text-right" value={newGasto.importe} onChange={e => setNewGasto({...newGasto, importe: e.target.value})} placeholder="0" data-testid="gasto-importe" />
          <select className="input-base text-xs" value={newGasto.categoria} onChange={e => setNewGasto({...newGasto, categoria: e.target.value})} data-testid="gasto-categoria">
            {CATEGORIAS_GASTO.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <div className="flex items-center justify-center">
            <button onClick={addGasto} disabled={!newGasto.concepto || !newGasto.importe} className="btn-primary px-2 py-1.5 text-sm disabled:opacity-40" data-testid="gasto-add-btn">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>}
      </div>

      {/* CALCULATE */}
      <AcquisitionExpenses asset={asset} assetId={assetId} readOnly={readOnly} onAssetUpdated={onAssetUpdated} />
      {!readOnly && (
      <button onClick={handleCalculate} disabled={readOnly || calculating || !form.tipo_activo_operativo} className="btn-primary w-full h-12 text-base flex items-center justify-center gap-2" data-testid="calculate-roi-btn">
        <Calculator className="w-5 h-5" />
        {calculating ? 'Calculando...' : 'Guardar y Calcular Rentabilidad'}
      </button>
      )}

      {/* RESULT — prioriza los valores del backend (result) sobre el cálculo local.
          Mientras el usuario no pulse "Calcular", `result` es null y se muestra la
          estimación local como preview. Al calcular, se sustituye por la respuesta
          oficial del backend, que es la fuente de verdad del ROI persistido. */}
      <div className="card-base p-6" data-testid="roi-result">
        <h3 className="heading-3 mb-5">Resultado del Análisis</h3>
        {(() => {
          if (!result && !hasRealRoiVenta(asset)) {
            return <div data-testid="roi-result-estimated"><p className="text-sm mb-2">ROI rápido estimado</p><RoiValue value={asset?.roi_rapido} isReal={false} testid="roi-result-estimated-value" /></div>;
          }
          const d = result || asset;
          const displayCosteTotal = d.coste_total ?? costeTotal;
          const displayRoiTotal   = d.roi_total ?? roiTotal;
          const displayRoiAnual   = d.roi_anualizado ?? (tiempoMeses > 0 ? displayRoiTotal * 12 / tiempoMeses : null);
          const displayImpuestos  = d.itp_estimado ?? totalImpuestos;
          const displayGastos     = d.gastos_totales ?? gastosTotal;
          const displayEquity     = (form.valor_salida_base || 0) - displayCosteTotal;
          const precioDisplay     = tipoOp === 'SUBASTA'
            ? (form.valor_adjudicacion || 0)
            : (form.precio_compra || 0);
          const precioLabel       = tipoOp === 'SUBASTA' ? 'Valor adjudicación' : 'Precio de compra';
          return (
            <div className="space-y-3">
              <ResultRow label={`${precioLabel}${asset?.precio_compra_es_estimado ? ' *' : ''}`} help={tipoOp === 'SUBASTA' ? "Precio pagado en la adjudicación. Base de coste para el ROI." : "Precio pagado por el activo. Base de coste para el ROI."} value={`${cur(precioDisplay)}${asset?.precio_compra_es_estimado ? ' *' : ''}`} />
              {tipoOp === 'OCUPADO' && (form.coste_posesion || 0) > 0 && (
                <ResultRow label="Coste posesión" help="Coste del procedimiento de desalojo (letrado, procurador, ejecución). Se suma al coste total en activos ocupados." value={cur(form.coste_posesion)} />
              )}
              {itpEnabled && itpAmount > 0 && <ResultRow label={`ITP (${itpRate}% sobre ${cur(baseItp)})`} value={cur2(itpAmount)} sub />}
              {ajdEnabled && ajdAmount > 0 && <ResultRow label={`AJD (${ajdRate}% sobre ${cur(baseAjd)})`} value={cur2(ajdAmount)} sub />}
              {ivaEnabled && ivaAmount > 0 && <ResultRow label={`IVA (${ivaRate}% sobre ${cur(baseIva)})`} value={cur2(ivaAmount)} sub />}
              {displayImpuestos > 0 && <ResultRow label="Total Impuestos" help="Suma de ITP, AJD e IVA aplicables a la operación." value={cur2(displayImpuestos)} bold />}
              <ResultRow label="Gastos totales" help="Total de gastos de operación (notaría, registro, gestoría, honorarios). Se editan en la sección Gastos de este bloque." value={cur2(displayGastos)} />
              <div className="border-t border-slate-300 my-2" />
              <div className="flex justify-between items-center py-2 px-4 bg-slate-100 rounded-lg">
                <span className="font-semibold text-slate-800">Coste Total</span>
                <span className="text-xl font-bold text-slate-900" data-testid="roi-result-coste-total">{cur(displayCosteTotal)}</span>
              </div>
              <ResultRow label={asset?.valoracion_resultado ? "Valor salida (individualizado)" : "Valor salida estimado"} value={cur(form.valor_salida_base)} />
              <div className={`flex justify-between items-center py-2 px-4 rounded-lg ${displayEquity >= 0 ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <span className={`font-semibold ${displayEquity >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>Equity (Beneficio)</span>
                <span className={`text-xl font-bold ${displayEquity >= 0 ? 'text-emerald-700' : 'text-red-700'}`} data-testid="roi-result-equity">{cur(displayEquity)}</span>
              </div>
              <div className="grid md:grid-cols-2 gap-3 mt-2">
                <div className={`p-4 rounded-lg ${displayRoiTotal >= 0 ? 'bg-blue-50 border border-blue-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`text-xs uppercase tracking-wide ${displayRoiTotal >= 0 ? 'text-blue-600' : 'text-red-600'}`}>ROI Total</p>
                  <p className={`text-2xl font-bold ${displayRoiTotal >= 0 ? 'text-blue-700' : 'text-red-700'}`} style={{fontFamily:'Outfit,sans-serif'}} data-testid="roi-result-roi-total">{pct(displayRoiTotal)}</p>
                </div>
                <div className={`p-4 rounded-lg ${displayRoiAnual != null ? (displayRoiAnual >= 0 ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200') : 'bg-slate-50 border border-slate-200'}`}>
                  <p className={`text-xs uppercase tracking-wide ${displayRoiAnual != null ? (displayRoiAnual >= 0 ? 'text-amber-600' : 'text-red-600') : 'text-slate-500'}`}>ROI Anualizado{result ? '' : ' Estimado'}</p>
                  {displayRoiAnual != null ? (
                    <p className={`text-2xl font-bold ${displayRoiAnual >= 0 ? 'text-amber-700' : 'text-red-700'}`} style={{fontFamily:'Outfit,sans-serif'}} data-testid="roi-result-roi-anualizado">{pct(displayRoiAnual)}</p>
                  ) : (
                    <p className="text-sm text-slate-500 mt-1">Introduce tiempo estimado (meses)</p>
                  )}
                  {tiempoMeses > 0 && !result && <p className="text-xs text-amber-500 mt-1">Basado en {tiempoMeses} meses estimados</p>}
                  {result?.dias_operacion && <p className="text-xs text-amber-500 mt-1">Basado en {result.dias_operacion} días reales</p>}
                </div>
              </div>
              {result?.formula_aplicada && (
                <p className="text-xs text-slate-500 mt-2" data-testid="roi-result-formula">{result.formula_aplicada}</p>
              )}
            </div>
          );
        })()}
      </div>

      {pdfModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="roi-export-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">Exportar análisis de rentabilidad</h3>
              <button onClick={() => setPdfModalOpen(false)} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Cerrar">×</button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                <label className="text-xs font-medium text-slate-600 flex items-center justify-between">
                  <span>Descripción de la oportunidad (opcional)</span>
                  <span className={`text-[10px] ${pdfDescription.length > 600 ? 'text-red-500' : 'text-slate-400'}`}>{pdfDescription.length}/600</span>
                </label>
                <textarea
                  value={pdfDescription}
                  onChange={(e) => setPdfDescription(e.target.value.slice(0, 600))}
                  rows={4}
                  maxLength={600}
                  placeholder="Añade una breve descripción para el inversor…"
                  className="input-base w-full text-sm mt-1"
                  data-testid="roi-export-description"
                />
                <p className="text-[10px] text-slate-400 mt-1">Recomendado: entre 300 y 600 caracteres. Si lo dejas vacío el PDF se genera sin bloque OPORTUNIDAD.</p>
              </div>

              {pdfPhotos.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-slate-600 flex items-center justify-between">
                    <span>Fotos <span className="text-slate-400 font-normal">(máx. 3)</span></span>
                    <span className="text-[10px] text-slate-400">
                      {pdfPhotos.filter(p => p.selected).length}/3 seleccionadas
                    </span>
                  </label>
                  <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1 border border-slate-100 rounded p-2 bg-slate-50">
                    {pdfPhotos.map((p, idx) => (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => togglePhotoSelected(idx)}
                        className={`relative aspect-square rounded overflow-hidden border-2 transition ${
                          p.selected ? 'border-amber-500 ring-2 ring-amber-300' : 'border-slate-200 hover:border-slate-400'
                        }`}
                        title={p.nombre}
                        data-testid={`roi-export-photo-${idx}`}
                      >
                        <img
                          src={p.url}
                          alt={p.nombre}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {p.selected && (
                          <span className="absolute top-1 right-1 bg-amber-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shadow">
                            {pdfPhotos.filter((pp, i) => pp.selected && i <= idx).length}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                onClick={() => setPdfModalOpen(false)}
                className="btn-secondary text-sm px-4 py-1.5"
                disabled={exporting}
                data-testid="roi-export-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={exportRoiPdf}
                disabled={exporting}
                className="btn-primary text-sm px-4 py-1.5 flex items-center gap-1.5"
                data-testid="roi-export-generate"
              >
                <FileDown className="w-3.5 h-3.5" /> {exporting ? 'Generando…' : 'Generar PDF'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RO = ({ label, help, value, tid, error }) => (
  <div className={`space-y-1 rounded-lg transition-all ${error ? 'field-error' : ''}`}>
    <label className={`text-sm ${error ? 'text-red-600 font-semibold' : 'text-slate-600'}`}>
      {label} {error ? '*' : ''}
      {help && <HelpHint text={help} />}
    </label>
    <p className={`text-sm font-medium py-2 ${error ? 'text-red-600' : 'text-slate-800'}`} data-testid={tid}>{value}</p>
    {error && <p className="text-xs text-red-500 font-medium">Campo obligatorio — editar desde ficha del activo</p>}
  </div>
);

const FN = ({ label, help, value, onChange, tid }) => (
  <div className="space-y-1">
    <label className="text-sm text-slate-600">{label}{help && <HelpHint text={help} />}</label>
    <input type="number" className="input-base" value={value || ''} onChange={e => onChange(e.target.value === '' ? 0 : parseFloat(e.target.value))} data-testid={tid} />
  </div>
);

const TaxRow = ({ label, help, enabled, setEnabled, rate, setRate, base, amount, children, ivaRates }) => (
  <div className={`p-4 rounded-lg border ${enabled ? 'border-blue-200 bg-blue-50/30' : 'border-slate-200 bg-slate-50/50'}`}>
    <div className="flex items-center gap-4">
      <label className="flex items-center gap-2 cursor-pointer min-w-[80px]">
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="w-4 h-4 rounded" data-testid={`tax-${label.toLowerCase()}-check`} />
        <span className={`text-sm font-semibold ${enabled ? 'text-blue-700' : 'text-slate-500'}`}>{label}{help && <HelpHint text={help} />}</span>
      </label>
      {enabled && (
        <>
          <div className="flex-1">{children}</div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Tipo:</span>
            {ivaRates ? (
              <select className="input-base w-44 text-sm" value={rate} onChange={e => setRate(parseFloat(e.target.value) || 0)} data-testid={`tax-${label.toLowerCase()}-rate`}>
                {ivaRates.map(r => (
                  <option key={r} value={r}>
                    {r === 0 ? '0% (Inversión Sujeto Pasivo)' : `${r}%`}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <input type="number" step="0.01" className="input-base w-20 text-center" value={rate || ''} onChange={e => setRate(parseFloat(e.target.value) || 0)} data-testid={`tax-${label.toLowerCase()}-rate`} />
                <span className="text-xs text-slate-500">%</span>
              </>
            )}
          </div>
          <div className="text-right min-w-[100px]">
            <p className="text-xs text-slate-500">Base: {cur(base)}</p>
            <p className="text-sm font-semibold text-slate-900">{cur2(amount)}</p>
          </div>
        </>
      )}
    </div>
  </div>
);

const ResultRow = ({ label, value, bold, sub, help }) => (
  <div className={`flex justify-between items-center py-1 ${sub ? 'pl-6' : ''}`}>
    <span className={`text-sm ${bold ? 'font-semibold text-slate-800' : sub ? 'text-slate-500' : 'text-slate-700'}`}>{label}{help && <HelpHint text={help} />}</span>
    <span className={`text-sm ${bold ? 'font-semibold text-slate-900' : 'text-slate-900 font-medium'}`}>{value}</span>
  </div>
);

export default ROIAdvancedTab;
